import React, {useState, useEffect, useMemo, useCallback} from "react";
import {Divider, Spin, message, Typography} from "antd";
import "../briefs.css";
import {NOOP} from "../../utils/NOOP.jsx";
import {generateSquareBriefInfo, generateBidAdditionalInfo} from "./utils.jsx";
import {isEmpty} from "lodash";
import {SquareBriefInfo} from "../utils.jsx";
import {BidPackageDetails} from "./BidPackageDetails.jsx";
import {MarketplaceImagesCarousel} from "../../marketplace/OrderImagesCarousel/MarketplaceImagesCarousel.jsx";
import {BidPricingInformation} from "./BidPricingInformation.jsx";
import {RequestBidCorrectionModal} from "../RequestBidCorrectionModal/RequestBidCorrectionModal.jsx";
import {StyledButton} from "../../components";
import {DownloadOutlined} from "@ant-design/icons";
import {openWindow} from "../../http/WindowOpener.jsx";
import {createUrlWithoutTransformations} from "../../utils/cloudinaryUtils.jsx";

const {Title} = Typography;

export const BriefDetailsView = ({
    onSendBid = NOOP,
    onCancel = NOOP,
    briefInfo,
    bidInfo,
    loadingBrief = false,
    onRequestCorrection = NOOP
}) => {
    const [brief, setBrief] = useState(null);
    const [formValues, setFormValues] = useState({});
    const [isCorrectionModalOpen, setIsCorrectionModalOpen] = useState(false);

    const orderSquareInfo = useMemo(() => generateSquareBriefInfo(brief, bidInfo), [brief, bidInfo]);
    const bidAdditionalInfo = useMemo(() => generateBidAdditionalInfo(bidInfo), [bidInfo]);

    useEffect(() => {
        try {
            setBrief(briefInfo);
            setFormValues(prev => ({...prev, dtstart: briefInfo?.dtstart}));
        } catch (err) {
            message.error("לא ניתן לטעון את פרטי הבריף");
        }
    }, [briefInfo]);

    useEffect(() => {
        if (bidInfo && !isEmpty(bidInfo)) {
            setFormValues(prev => ({...prev, ...bidInfo}));
        }
    }, [bidInfo]);

    const onSaveBid = useCallback(() => {
        onSendBid({
            ...formValues,
            briefId: brief?.briefId,
            address: brief?.address,
            participantsAmount: Number(formValues.participantsAmount),
            deliveryPrice: Number(formValues.deliveryPrice),
            providerId: bidInfo?.providerId,
            systemId: brief?.systemId,
            package: {
                ...formValues?.package,
                options: formValues?.package?.options.map(({key, amount, price, ...rest}) => ({
                    ...rest,
                    amount: Number(amount),
                    price: Number(price)
                }))
            }
        });
    }, [bidInfo?.providerId, brief?.address, brief?.briefId, brief?.systemId, formValues, onSendBid]);

    const handleRequestCorrection = useCallback(
        async correctionDetails => {
            await onRequestCorrection(correctionDetails);
            setIsCorrectionModalOpen(false);
        },
        [onRequestCorrection]
    );

    if (loadingBrief) {
        return (
            <div className="provider-brief-details-card" style={{textAlign: "center", padding: 20}}>
                <Spin size="large" />
            </div>
        );
    }

    if (!brief) {
        return (
            <div className="provider-brief-details-card" style={{padding: 20}}>
                <p>פרטי הבריף לא נמצאו.</p>
            </div>
        );
    }

    return (
        <div className="provider-brief-details-card">
            <div className="provider-brief-header">
                {bidInfo?.note && (
                    <div className="bid-note-container">
                        <div className="bid-note-title">התיקונים שביקש הלקוח</div>
                        <div className="bid-note-content">{bidInfo?.note}</div>
                    </div>
                )}
                <div className="provider-brief-title">
                    <span className="brief-title">פרטי ההזמנה לאירוע</span>
                </div>
                <div className="provider-brief-summary">
                    {orderSquareInfo?.map(({row = []}, index) => {
                        return <SquareBriefInfo key={index} row={row} />;
                    })}
                </div>
            </div>
            <Divider style={{margin: "12px 0"}} />
            <div className="provider-brief-title">
                <span className="brief-title">תוכן הצעת המחיר </span>
            </div>
            {bidInfo?.package?.options?.length > 0 ? (
                <BidPackageDetails bidInfo={bidInfo} />
            ) : bidInfo?.package?.files?.length > 0 ? (
                <div className="bid-files-download-section">
                    <div className="bid-files-title">הורדת קובץ PDF / Excel</div>
                    {bidInfo.package.files.map((file, index) => (
                        <div key={index} className="bid-file-download-item">
                            <span className="bid-file-name">{file.fileName}</span>
                            <span
                                className="bid-file-download-link"
                                onClick={() => openWindow(createUrlWithoutTransformations(file.fileUrl))}>
                                <DownloadOutlined style={{marginLeft: 5}} />
                                לחץ כאן להורדה
                            </span>
                        </div>
                    ))}
                    <div className="total-price-sum">סכום הצעת מחיר: {`${bidInfo.price} ₪`}</div>
                </div>
            ) : null}
            {bidAdditionalInfo.length > 0 && (
                <>
                    <Divider style={{margin: "12px 0"}} />
                    {bidAdditionalInfo.map(({row = []}, index) => (
                        <SquareBriefInfo key={index} row={row} />
                    ))}
                </>
            )}
            <Divider style={{margin: "12px 0"}} />
            {!!bidInfo?.package?.photos?.length && (
                <>
                    <Title className="bid-form-titles">גלריית תמונות</Title>
                    <MarketplaceImagesCarousel
                        log={NOOP}
                        images={bidInfo?.package?.photos.map(p => ({id: p.imageId, url: p.imageUrl, key: p.imageUrl}))}
                        showCarouselArrows={bidInfo?.package?.photos?.length > 1}
                        carouselProps={{infinite: true}}
                    />
                </>
            )}
            <Divider style={{margin: "12px 0"}} />
            <BidPricingInformation bidInfo={bidInfo} />
            <Divider style={{margin: "12px 0"}} />

            <div className="bid-actions">
                <StyledButton className="add-item-btn" onClick={onSaveBid}>
                    אישור הצעה
                </StyledButton>
                <StyledButton
                    className="add-item-btn send-correction-btn"
                    onClick={() => setIsCorrectionModalOpen(true)}>
                    שליחת תיקונים לבית העסק
                </StyledButton>
            </div>
            <RequestBidCorrectionModal
                visible={isCorrectionModalOpen}
                onClose={() => setIsCorrectionModalOpen(false)}
                onSave={handleRequestCorrection}
                bidInfo={bidInfo}
            />
        </div>
    );
};
